import { FormEvent, useState } from 'react'
import type { Course } from '@/data/catalog'
import { createCourseAssignment, uploadAssignmentFile, type AssignmentKind, type CourseAssignment, type QuizQuestion } from '@/lib/assignments'

interface AssignmentFormProps {
  course: Course
  creator: string
  defaultModuleId?: string
  onCreated: (record: CourseAssignment) => void
}

const MAX_QUESTIONS = 10
const OPTIONS_PER_QUESTION = 3

function emptyQuestion(): QuizQuestion {
  return { question: '', options: ['', '', ''], correctIndex: 0 }
}

function AssignmentForm({ course, creator, defaultModuleId = '', onCreated }: AssignmentFormProps) {
  const [kind, setKind] = useState<AssignmentKind>('assignment')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [moduleId, setModuleId] = useState(defaultModuleId || course.modules[0]?.id || '')
  const [dueLabel, setDueLabel] = useState('')
  const [externalUrl, setExternalUrl] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const [fileName, setFileName] = useState('')
  const [questions, setQuestions] = useState<QuizQuestion[]>([emptyQuestion()])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  const addQuestion = () => {
    setQuestions((list) => (list.length >= MAX_QUESTIONS ? list : [...list, emptyQuestion()]))
  }

  const removeQuestion = (index: number) => {
    setQuestions((list) => list.filter((_, i) => i !== index))
  }

  const updateQuestionText = (index: number, value: string) => {
    setQuestions((list) => list.map((item, i) => (i === index ? { ...item, question: value } : item)))
  }

  const updateOptionText = (index: number, optionIndex: number, value: string) => {
    setQuestions((list) =>
      list.map((item, i) => {
        if (i !== index) return item
        const options = [...item.options]
        options[optionIndex] = value
        return { ...item, options }
      }),
    )
  }

  const updateCorrectIndex = (index: number, optionIndex: number) => {
    setQuestions((list) => list.map((item, i) => (i === index ? { ...item, correctIndex: optionIndex } : item)))
  }

  const onUpload = async (file?: File) => {
    if (!file) return
    setError('')
    setMessage('')
    setBusy(true)
    try {
      const uploaded = await uploadAssignmentFile(file)
      setFileUrl(uploaded.url)
      setFileName(uploaded.name)
      setMessage('Archivo subido. Publica para que el alumnado lo vea.')
    } catch (err) {
      console.error('No se pudo subir el archivo:', err)
      setError(err instanceof Error ? err.message : 'No se pudo subir el archivo.')
    } finally {
      setBusy(false)
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!title.trim()) {
      setError('Escribe un título.')
      return
    }
    if (!moduleId) {
      setError('Elige el módulo de esta actividad.')
      return
    }
    let cleanQuestions: QuizQuestion[] = []
    if (kind === 'quiz') {
      if (questions.length === 0) {
        setError('Añade al menos una pregunta al quiz.')
        return
      }
      for (const item of questions) {
        if (!item.question.trim()) {
          setError('Cada pregunta necesita un enunciado.')
          return
        }
        if (item.options.some((option) => !option.trim())) {
          setError('Completa las 3 opciones de cada pregunta.')
          return
        }
      }
      cleanQuestions = questions.map((item) => ({
        question: item.question.trim(),
        options: item.options.map((option) => option.trim()),
        correctIndex: item.correctIndex,
      }))
    }
    setError('')
    setMessage('')
    setBusy(true)
    try {
      const record = await createCourseAssignment({
        courseId: course.id,
        courseSlug: course.slug,
        moduleId,
        kind,
        title,
        description,
        fileUrl,
        fileName,
        externalUrl,
        dueLabel,
        questions: cleanQuestions,
        creator,
      })
      onCreated(record)
      setTitle('')
      setDescription('')
      setDueLabel('')
      setExternalUrl('')
      setFileUrl('')
      setFileName('')
      setQuestions([emptyQuestion()])
      setMessage('Actividad publicada en el módulo.')
    } catch (err) {
      console.error('No se pudo publicar la actividad:', err)
      setError('No se pudo publicar. Comprueba que tu cuenta es administradora.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-4 space-y-3 border border-[#1E3A5F]/10 p-4">
      <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">Subir assignment de este módulo</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-xs">
          Tipo
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value as AssignmentKind)}
            className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
          >
            <option value="assignment">Assignment</option>
            <option value="quiz">Quiz</option>
          </select>
        </label>
        <label className="block text-xs">
          Módulo
          <select
            value={moduleId}
            onChange={(event) => setModuleId(event.target.value)}
            required
            className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
          >
            {course.modules.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="block text-xs">
        Título
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          required
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Enunciado
        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          rows={3}
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Fecha límite (texto)
        <input
          value={dueLabel}
          onChange={(event) => setDueLabel(event.target.value)}
          placeholder="19 sep 2026, 14:00 CDMX"
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Liga externa (Google Forms, Earth Engine, etc.)
        <input
          type="url"
          value={externalUrl}
          onChange={(event) => setExternalUrl(event.target.value)}
          className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
        />
      </label>
      <label className="block text-xs">
        Archivo del quiz o assignment
        <input
          type="file"
          onChange={(event) => void onUpload(event.target.files?.[0])}
          className="mt-1 block w-full text-sm file:mr-3 file:border file:border-[#1E3A5F]/20 file:bg-transparent file:px-3 file:py-1.5"
        />
      </label>
      {fileName ? <p className="text-xs text-[#1E3A5F]/60">Adjunto: {fileName}</p> : null}

      {kind === 'quiz' ? (
        <div className="space-y-4 border-t border-[#1E3A5F]/10 pt-4">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.18em] text-[#C41E3A]">
              Preguntas ({questions.length}/{MAX_QUESTIONS})
            </p>
            <button
              type="button"
              onClick={addQuestion}
              disabled={questions.length >= MAX_QUESTIONS}
              className="text-xs underline disabled:opacity-40"
            >
              Añadir pregunta
            </button>
          </div>
          {questions.map((item, index) => (
            <div key={index} className="space-y-2 border border-[#1E3A5F]/10 p-3">
              <div className="flex items-start justify-between gap-2">
                <label className="block flex-1 text-xs">
                  Pregunta {index + 1}
                  <input
                    value={item.question}
                    onChange={(event) => updateQuestionText(index, event.target.value)}
                    className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
                  />
                </label>
                {questions.length > 1 ? (
                  <button
                    type="button"
                    onClick={() => removeQuestion(index)}
                    className="mt-5 text-xs text-[#C41E3A]"
                  >
                    Quitar
                  </button>
                ) : null}
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {Array.from({ length: OPTIONS_PER_QUESTION }).map((_, optionIndex) => (
                  <label key={optionIndex} className="block text-xs">
                    <span className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        name={`correct-${index}`}
                        checked={item.correctIndex === optionIndex}
                        onChange={() => updateCorrectIndex(index, optionIndex)}
                      />
                      Opción {optionIndex + 1}
                    </span>
                    <input
                      value={item.options[optionIndex]}
                      onChange={(event) => updateOptionText(index, optionIndex, event.target.value)}
                      className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2 text-sm"
                    />
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#1E3A5F]/50">Marca la opción correcta con el punto de radio.</p>
            </div>
          ))}
        </div>
      ) : null}

      {error ? <p className="text-xs text-[#C41E3A]">{error}</p> : null}
      {message ? <p className="text-xs text-[#1E3A5F]/65">{message}</p> : null}
      <button type="submit" disabled={busy} className="bg-[#1E3A5F] px-3 py-2 text-sm text-[#F4EFE6] disabled:opacity-50">
        {busy ? 'Publicando…' : 'Publicar en el módulo'}
      </button>
    </form>
  )
}

export default AssignmentForm
